import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u6_x3_stb';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  {
    heading: 'Einführung',
    body: 'Sicherheitstemperaturwächter (STW) und Sicherheitstemperaturbegrenzer (STB) sind unverzichtbare Schutzeinrichtungen in Heizungs- und Kälteanlagen. Sie verhindern Schäden und gefährliche Betriebszustände durch Überhitzung.'
  },
  {
    heading: 'STW vs. STB – Unterschiede',
    body: 'STW: Abschalttemperatur einstellbar; Wiedereinschaltung automatisch nach Abkühlung (Schaltdifferenz); Funktion als Schutz/Begrenzung, z. B. Vorlauf FBH auf 55°C.\n\nSTB: Abschalttemperatur fest vorgegeben (oft plombiert); Wiedereinschaltung nur per manueller Entriegelung (Reset) am Gerät nach Behebung der Ursache; letzte Sicherheitsinstanz, z. B. Kesselschutz 110–120°C.'
  },
  {
    heading: 'Auslösung und Reaktion der Regelung',
    body: 'Typisch: Wärmeerzeuger/Kälteerzeuger wird abgeschaltet (Unterbrechung Steuerstromkreis), DDC erhält Meldung.\n\nDDC-Reaktion (Heizungsfall):\n1) Heizventile schließen (Wärmezufuhr stoppen bzw. Wärme im Erzeuger halten).\n2) Pumpen in Betrieb lassen (Restwärme sicher abführen).\n3) Störmeldung/Alarm generieren.\n4) Verriegelung bei STB (kein neuer Anforderungsstart bis Hardware-Reset und Quittierung).'
  },
  {
    heading: 'Anschluss & Quittierung',
    body: 'Elektrischer Anschluss: Potentialfreie Kontakte (Öffner/Wechsler). Sicherheitsrelevanter Kontakt in Reihe zur Brennerfreigabe/Heizpatronensteuerung, parallele Meldung an DI der DDC.\n\nQuittierung: STW – automatisch nach Rückkehr unter Rückschaltpunkt (Meldung ggf. quittieren). STB – zwingend manuelle Entriegelung am Gerät; erst nach Ursachenbehebung und Rückmeldung in der DDC darf der Betrieb wieder aufgenommen werden.'
  },
  {
    heading: 'Fazit',
    body: 'Die korrekte Auswahl, Positionierung und Verdrahtung von STW/STB inkl. sauberer Verriegelungslogik in der DDC ist essenziell für sicheren, vorschriftskonformen Betrieb.'
  }
];

const images = [
  { id: 'img_stx_beispiel', label: 'Typische Montage/Visualisierung', src: `${IMAGE_BASE}tv_visu_hkk.PNG`, alt: 'STB/STW an Heizkessel – Beispiel' },
  { id: 'img_stx_schaltplan_anschluss', label: 'Schaltplan Anschluss', src: `${IMAGE_BASE}schaltplan_stb_sep.PNG`, alt: 'Schaltplan: Anschluss STB im Steuerkreis' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.6.3 STB (STW & STB)', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u6_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


